import type { BranchKey, WorkspaceKey } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useChangesetStateContext } from '../../context/changesetStateContext'
import { useLayoutContext } from '../../context/layoutContext'
import { useFetchBranchChangesets } from '../api/changesets/useFetchBranchChangesets'
import { useFetchWorkspaceBaseBranchChangesets } from '../api/changesets/useFetchWorkspaceBaseBranchChangesets'

export const useChangesetsWithHistory = ({
  branchKey,
  workspaceKey,
  isDisabledChangesetsRelevant,
}: {
  workspaceKey: Optional<WorkspaceKey>
  branchKey: Optional<BranchKey>
  isDisabledChangesetsRelevant: boolean
}) => {
  const {
    preferences: {
      parameters: { isShowingExtrinsicParameterValues },
    },
  } = useLayoutContext()
  const { disabledChangesetKeys } = useChangesetStateContext()
  // The query results have to be destructured here rather than kept whole and
  // depended on below, as react-query hands out a new result object on every
  // render. Depending on the objects makes the memo below miss every time, which
  // gives the changesets a new identity on every render and cascades into an
  // infinite render loop through the operations context.
  const {
    data: fetchedBranchChangesets,
    isFetching: isFetchingBranchChangesets,
    isLoading: isLoadingBranchChangesets,
  } = useFetchBranchChangesets({
    enabled: !workspaceKey,
    branchKey,
    includeBaseChangesets: true,
    shouldIncludeExtrinsicParameterValues: isShowingExtrinsicParameterValues,
  })

  const {
    data: fetchedWorkspaceBranchChangesets,
    isFetching: isFetchingWorkspaceBranchChangesets,
    isLoading: isLoadingWorkspaceBranchChangesets,
  } = useFetchWorkspaceBaseBranchChangesets(workspaceKey)

  return useMemo(() => {
    const [allChangesets, isFetching, isLoading] = workspaceKey
      ? ([
          fetchedWorkspaceBranchChangesets,
          isFetchingWorkspaceBranchChangesets,
          isLoadingWorkspaceBranchChangesets,
        ] as const)
      : ([
          fetchedBranchChangesets,
          isFetchingBranchChangesets,
          isLoadingBranchChangesets,
        ] as const)

    return {
      data: allChangesets?.branchChangesets
        ?.filter(notEmpty)
        .filter(
          (changesetWithHistory) =>
            !isDisabledChangesetsRelevant ||
            !changesetWithHistory.changeset?.changesetKey ||
            !disabledChangesetKeys.includes(
              changesetWithHistory.changeset?.changesetKey,
            ),
        ),
      isFetching,
      /** Whether the changesets are being loaded for the first time, with nothing to show yet. */
      isLoading,
    }
  }, [
    disabledChangesetKeys,
    fetchedBranchChangesets,
    fetchedWorkspaceBranchChangesets,
    isDisabledChangesetsRelevant,
    workspaceKey,
    isFetchingBranchChangesets,
    isFetchingWorkspaceBranchChangesets,
    isLoadingBranchChangesets,
    isLoadingWorkspaceBranchChangesets,
  ])
}
